import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Tooltip } from '@mui/material';
import { sidebar } from '@/styles/tokens';
import { MODES } from './sidebar.config';
import { SidebarMode } from './sidebar.types';

interface SidebarModeSwitcherProps {
  currentMode: SidebarMode;
  availableModes: SidebarMode[];
  isCollapsed: boolean;
  isMobile: boolean;
  onSelect: (mode: SidebarMode) => void;
}

const COLUMNS = 5;

export const SidebarModeSwitcher: React.FC<SidebarModeSwitcherProps> = ({
  currentMode,
  availableModes,
  isCollapsed,
  isMobile,
  onSelect,
}) => {
  const { t } = useTranslation();
  const isLight = currentMode === 'general' || currentMode === 'client' || currentMode === 'expert';
  const columns = isCollapsed && !isMobile ? 1 : COLUMNS;

  return (
    <Box
      sx={{
        px: 0.5,
        py: 1,
        borderTop: 1,
        borderColor: isLight ? 'divider' : sidebar.darkDivider,
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 0.25,
      }}
    >
      {availableModes.map(mode => {
        const config = MODES[mode];
        const isActive = mode === currentMode;

        return (
          <Tooltip key={mode} title={t(config.label)} placement={columns === 1 ? 'right' : 'top'}>
            <IconButton
              aria-label={t(config.label)}
              aria-pressed={isActive}
              onClick={() => !isActive && onSelect(mode)}
              sx={{
                borderRadius: 1,
                height: 40,
                minWidth: 0,
                p: 0,
                color: isLight ? (isActive ? 'primary.main' : 'text.secondary') : sidebar.darkText,
                opacity: !isLight && !isActive ? 0.6 : 1,
                border: 1,
                borderColor: isActive ? (isLight ? 'primary.main' : sidebar.darkBorderHover) : 'transparent',
                backgroundColor: isActive ? (isLight ? 'action.selected' : sidebar.darkSelected) : undefined,
                '&:hover': {
                  opacity: 1,
                  backgroundColor: isLight ? 'action.hover' : sidebar.darkHover,
                },
              }}
            >
              {config.icon}
            </IconButton>
          </Tooltip>
        );
      })}
    </Box>
  );
};
